import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ChevronDown, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { User } from '@/types/auth';

type Props = {
  salespeople: User[];
  selected: string[];
  onChange: (ids: string[]) => void;
};

export default function SalespersonSelect({ salespeople, selected, onChange }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const selectedPeople = useMemo(
    () => salespeople.filter((sp) => selected.includes(sp.id)),
    [salespeople, selected],
  );

  const available = useMemo(() => {
    const unselected = salespeople.filter((sp) => !selected.includes(sp.id));
    if (!search.trim()) return unselected;
    const q = search.toLowerCase();
    return unselected.filter(
      (sp) =>
        sp.firstName.toLowerCase().includes(q) ||
        sp.lastName.toLowerCase().includes(q) ||
        sp.email.toLowerCase().includes(q),
    );
  }, [salespeople, selected, search]);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const add = (id: string) => {
    onChange([...selected, id]);
    setSearch('');
  };

  const remove = (id: string) => {
    onChange(selected.filter((x) => x !== id));
  };

  return (
    <div ref={ref} className="relative">
      {/* Selected chips */}
      {selectedPeople.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {selectedPeople.map((sp) => (
            <Badge key={sp.id} variant="secondary" className="gap-1 pr-1">
              {sp.firstName} {sp.lastName}
              <button
                type="button"
                onClick={() => remove(sp.id)}
                className="ml-0.5 rounded-sm p-0.5 text-muted-foreground hover:text-destructive transition-colors"
                aria-label={t('common.remove')}
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Trigger */}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full justify-between text-muted-foreground"
        onClick={() => setOpen(!open)}
      >
        {t('configurator.addSalesperson', 'Añadir vendedor...')}
        <ChevronDown className="size-3.5" />
      </Button>

      {/* Dropdown */}
      {open && (
        <div className="absolute left-0 top-full z-40 mt-1 w-full rounded-lg border border-border bg-card shadow-lg animate-fade-in">
          {/* Search */}
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <Search className="size-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('common.search', 'Buscar...')}
              className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground/50 outline-none"
            />
          </div>
          {/* List */}
          <div className="max-h-48 overflow-y-auto">
            {available.length === 0 ? (
              <p className="px-3 py-4 text-center text-sm text-muted-foreground">
                {search
                  ? t('common.noResults', 'Sin resultados')
                  : t('configurator.allAssigned', 'Todos asignados')}
              </p>
            ) : (
              available.map((sp) => (
                <button
                  key={sp.id}
                  type="button"
                  onClick={() => add(sp.id)}
                  className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-muted"
                >
                  <span className="font-medium text-foreground">{sp.firstName} {sp.lastName}</span>
                  <span className="text-xs text-muted-foreground">{sp.email}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
